import { useMemo } from 'react'
import { Separator } from '@/components/ui/separator'
import { useCategoryDetail } from './context'
import { formatAmount } from '@/lib/utils'

type Props = {
  daysInMonth: number
}

export function CategoryDailyBarsSection({ daysInMonth }: Props) {
  const { categoryName, categoryColor, filteredTransactions } = useCategoryDetail()

  const dailyData = useMemo(() => {
    const map = new Map<number, number>()
    for (const tx of filteredTransactions) {
      let amount = 0
      for (const item of tx.items) {
        if (item.parent_category_name === categoryName) {
          amount += item.amount
        }
      }
      if (amount === 0) continue
      const day = parseInt(tx.date.split('-')[2], 10)
      if (!Number.isFinite(day)) continue
      map.set(day, (map.get(day) ?? 0) + amount)
    }
    const bars = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1
      return { day, amount: map.get(day) ?? 0 }
    })
    const maxAmount = Math.max(0, ...bars.map((b) => b.amount))
    return { bars, maxAmount }
  }, [filteredTransactions, categoryName, daysInMonth])

  if (dailyData.maxAmount === 0) return null

  return (
    <>
      <Separator />
      <div className="space-y-1.5">
        <h3 className="text-sm font-medium text-muted-foreground">日別</h3>
        <div className="flex h-12 gap-px">
          {dailyData.bars.map(({ day, amount }) => {
            const heightPct =
              dailyData.maxAmount > 0
                ? Math.max((amount / dailyData.maxAmount) * 100, amount > 0 ? 6 : 0)
                : 0
            return (
              <div
                key={day}
                className="relative h-full flex-1 min-w-px"
                title={amount > 0 ? `${day}日: ${formatAmount(amount)}` : `${day}日`}
              >
                {amount > 0 && (
                  <div
                    className="absolute inset-x-0 bottom-0 rounded-sm"
                    style={{
                      height: `${heightPct}%`,
                      backgroundColor: categoryColor,
                      opacity: 0.8,
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
          <span>1日</span>
          <span>{daysInMonth}日</span>
        </div>
      </div>
    </>
  )
}
